 import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';
import AuthPage from '@/pages/AuthPage';
import LoginPage from '@/pages/LoginPage';
import Dashboard from '@/pages/Dashboard';
import Transactions from '@/pages/Transactions';
import Accounts from '@/pages/Accounts';
import AccountDetails from '@/pages/AccountDetails';
import InstallPWA from '@/pages/InstallPWA';
import PaymentReminders from '@/pages/PaymentReminders';
import Categories from '@/pages/Categories';
import Statistics from '@/pages/Statistics';
import AdminPanel from '@/pages/AdminPanel';
import Profile from '@/pages/Profile';
import VerifyEmail from '@/pages/VerifyEmail';
import ResetPassword from '@/pages/ResetPassword';
import Pricing from '@/pages/Pricing';
// Subscription ahora integrado en Profile
import VoiceCredits from '@/pages/VoiceCredits';
import AIInsightsImproved from '@/pages/AIInsightsImproved';
import FeedbackSupport from '@/pages/FeedbackSupport';
import AIFinancieraInfo from '@/pages/AIFinancieraInfo';
import VozInfo from '@/pages/VozInfo';
import TarjetasMsiInfo from '@/pages/TarjetasMsiInfo';
import DeudasSpeiInfo from '@/pages/DeudasSpeiInfo';
import Goals from '@/pages/Goals';
import ObjetivosInfo from '@/pages/ObjetivosInfo';
import Debts from '@/pages/Debts';
import PublicDebt from '@/pages/PublicDebt';
import PublicSharedExpense from '@/pages/PublicSharedExpense';
import AdvancedAnalytics from '@/pages/AdvancedAnalytics';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import Terms from '@/pages/Terms';
import About from '@/pages/About';
import Contact from '@/pages/Contact';
import GoogleCallback from '@/pages/GoogleCallback';
import InactivityLogout from '@/components/InactivityLogout';
import AutoNotificationHandler from '@/components/AutoNotificationHandler';
import { Toaster } from '@/components/ui/sonner';
import { toasterConfig } from '@/lib/toast-config';
import { isAppMode } from '@/utils/pwa-utils';
import PaymentLockOverlay from '@/components/PaymentLockOverlay';
import PWAInstallBanner from '@/components/PWAInstallBanner';
import PWAUpdateNotification from '@/components/PWAUpdateNotification';
import OnboardingTour from '@/components/OnboardingTour';

const BACKEND_URL = (process.env.NODE_ENV === 'production' || (typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.()))
  ? (process.env.REACT_APP_BACKEND_URL && !process.env.REACT_APP_BACKEND_URL.includes('localhost')
      ? process.env.REACT_APP_BACKEND_URL
      : 'https://pgastos.capacitatu.com')
  : (process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001');
export const API = `${BACKEND_URL}/api`;

// Configurar timeout global para evitar peticiones colgadas de minutos
axios.defaults.timeout = 15000;

export const AuthContext = React.createContext();

// Componente interno para manejar scroll en cambios de ruta
function KeyboardFocusHandler() {
  useEffect(() => {
    const handleFocusIn = (e) => {
      const target = e.target;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) {
        if (target.type === 'checkbox' || target.type === 'radio' || target.type === 'file' || target.type === 'submit') return;
        setTimeout(() => {
          target.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
        }, 180);
        setTimeout(() => {
          target.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
        }, 350);
      }
    };

    window.addEventListener('focusin', handleFocusIn);
    return () => window.removeEventListener('focusin', handleFocusIn);
  }, []);

  return null;
}

function ScrollHandler() {
  const location = useLocation();
  const prevPathname = useRef(location.pathname);

  useEffect(() => {
    if (prevPathname.current !== location.pathname) {
      prevPathname.current = location.pathname;
      if (location.pathname === '/accounts' && sessionStorage.getItem('return_to_account_id')) {
        // No forzar scroll al tope si se va a restaurar la posición de la cuenta visitada
        return;
      }
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }
  }, [location.pathname]);

  return null;
}

// Ruta raíz inteligente: no enviar a dashboard si el usuario solicita ver precios (?pricing=true) o registrarse (?register=true)
const RootHomeRoute = ({ token, appMode }) => {
  const [searchParams] = useSearchParams();
  const hasPricingParam = searchParams.get('pricing') === 'true';
  const hasRegisterParam = searchParams.get('register') === 'true' || searchParams.get('mode') === 'register';
  const hasLoginParam = searchParams.get('login') === 'true' || searchParams.get('mode') === 'login';

  if (!token || hasPricingParam || hasRegisterParam || hasLoginParam) {
    return appMode && !hasPricingParam && !hasRegisterParam ? <LoginPage /> : <AuthPage />;
  }
  return <Navigate to="/dashboard" replace />;
};

function App() {
  const [user, setUser] = useState(() => {
    try {
      const cached = localStorage.getItem('user_cache');
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });
  const [token, setToken] = useState(localStorage.getItem('token'));
  // Si ya tenemos token y usuario en caché, no bloqueamos la interfaz con pantalla de carga
  const [loading, setLoading] = useState(() => {
    const t = localStorage.getItem('token');
    const u = localStorage.getItem('user_cache');
    return Boolean(t && !u);
  });
  const [appMode, setAppMode] = useState(isAppMode());

  useEffect(() => {
    const checkMode = () => {
      const currentMode = isAppMode();
      if (window.location.search.includes('display=standalone') && !currentMode) {
        const newUrl = window.location.pathname + window.location.search.replace(/[?&]display=standalone/, '').replace(/^&/, '?');
        window.history.replaceState({}, document.title, newUrl || '/');
      }
      if (currentMode !== appMode) {
        setAppMode(currentMode);
      }
    };
    
    checkMode();
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const handleModeChange = (e) => {
      if (e.matches) setAppMode(true);
      else checkMode();
    };

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleModeChange);
    }
    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleModeChange);
      }
    };
  }, [appMode]);

  useEffect(() => {
    // Manejo del botón Atrás y Deep Links / App Links en Android
    const setupCapacitorApp = async () => {
      if (window.Capacitor) {
        try {
          const { App: CapApp } = await import('@capacitor/app');

          CapApp.addListener('backButton', ({ canGoBack }) => {
            const path = window.location.pathname;
            // Si estamos en una ruta raíz o de inicio, no cerrar la app
            if (path === '/dashboard' || path === '/' || path === '/login') {
              console.log('Back button disabled on root page');
            } else {
              window.history.back();
            }
          });

          const handleIncomingUrl = (urlString) => {
            if (!urlString) return;
            try {
              // Soporta https://pgastos.capacitatu.com/verify-email?token=... o portalgastos://...
              const urlObj = new URL(urlString);
              const pathWithQuery = urlObj.pathname + urlObj.search;
              if (pathWithQuery && pathWithQuery !== '/' && pathWithQuery !== '/open-app') {
                window.location.href = pathWithQuery;
              }
            } catch (err) {
              const match = urlString.match(/^[a-zA-Z0-9_-]+:\/\/[^/]+(\/.*)$/);
              if (match && match[1] && match[1] !== '/' && match[1] !== '/open-app') {
                window.location.href = match[1];
              }
            }
          };

          // 1. Manejar link si la app se abrió en frío desde el enlace del correo
          CapApp.getLaunchUrl().then(launchData => {
            if (launchData && launchData.url) {
              handleIncomingUrl(launchData.url);
            }
          }).catch(() => {});

          // 2. Manejar link si la app ya estaba abierta en segundo plano
          CapApp.addListener('appUrlOpen', (data) => {
            if (data && data.url) {
              handleIncomingUrl(data.url);
            }
          });
        } catch (error) {
          console.error('Error configurando Capacitor App listeners:', error);
        }
      }
    };
    setupCapacitorApp();
  }, []);

  useEffect(() => {
    const handleAppInstalled = () => {
      localStorage.setItem('pwa_installed_success', 'true');
      setAppMode(true);
    };
    window.addEventListener('appinstalled', handleAppInstalled);
    return () => window.removeEventListener('appinstalled', handleAppInstalled);
  }, []);

  useEffect(() => {
    if (token) {
      axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 8000
      })
        .then(res => {
          setUser(res.data);
          try {
            localStorage.setItem('user_cache', JSON.stringify(res.data));
          } catch {}
          setLoading(false);
        })
        .catch((error) => {
          if (error.response && (error.response.status === 401 || error.response.status === 403)) {
            localStorage.removeItem('token');
            localStorage.removeItem('user_cache');
            setToken(null);
            setUser(null);
          }
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [token]);

  const login = async (newToken, userData) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    if (userData) {
      setUser(userData);
      try { localStorage.setItem('user_cache', JSON.stringify(userData)); } catch {}
    }
    try {
      const res = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${newToken}` },
        timeout: 8000
      });
      setUser(res.data);
      try { localStorage.setItem('user_cache', JSON.stringify(res.data)); } catch {}
    } catch (error) {
      if (userData) setUser(userData);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user_cache');
    setToken(null);
    setUser(null);
  };

  const refreshUser = useCallback(async () => {
    if (token) {
      try {
        const res = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(res.data);
      } catch (error) {
        console.error('Error refreshing user:', error);
      }
    }
  }, [token]);

  // Vinculación automática de deuda pendiente (Claim Token) al iniciar sesión o registrarse
  useEffect(() => {
    if (token) {
      const claimToken = localStorage.getItem('pending_debt_claim_token');
      if (claimToken) {
        axios.post(`${API}/shared-expenses/claim/${claimToken}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        }).then(() => {
          localStorage.removeItem('pending_debt_claim_token');
          window.location.href = '/debts';
        }).catch((err) => {
          console.error('Error vinculando deuda compartida:', err);
          localStorage.removeItem('pending_debt_claim_token');
          window.location.href = '/debts';
        });
      }
    }
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="text-lg text-slate-600">Cargando...</div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, token, login, logout, refreshUser }}>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <KeyboardFocusHandler />
        <ScrollHandler />
        <InactivityLogout />
        <AutoNotificationHandler />
        <Routes>
          <Route path="/" element={<RootHomeRoute token={token} appMode={appMode} />} />
          <Route path="/login" element={!token ? <LoginPage /> : <Navigate to="/dashboard" />} />
          <Route path="/register" element={!token ? <LoginPage /> : <Navigate to="/dashboard" />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/open-app" element={<Navigate to="/" replace />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/auth/google/callback" element={<GoogleCallback />} />
          <Route path="/ai-financiera-info" element={<AIFinancieraInfo />} />
          <Route path="/objetivos-info" element={<ObjetivosInfo />} />
          <Route path="/voz-info" element={<VozInfo />} />
          <Route path="/tarjetas-msi-info" element={<TarjetasMsiInfo />} />
          <Route path="/deudas-info" element={<DeudasSpeiInfo />} />
          <Route path="/subscription" element={<Navigate to="/profile?tab=subscription" replace />} />
          <Route path="/voice-credits/purchase" element={token ? <VoiceCredits /> : <Navigate to="/" />} />
          <Route path="/voice-credits/success" element={token ? <VoiceCredits /> : <Navigate to="/" />} />
          
          {/* Rutas protegidas */}
          <Route path="/dashboard" element={token ? <Dashboard /> : <Navigate to="/" />} />
          <Route path="/transactions" element={token ? <Transactions /> : <Navigate to="/" />} />
          <Route path="/accounts" element={token ? <Accounts /> : <Navigate to="/" />} />
          <Route path="/accounts/:accountId" element={token ? <AccountDetails /> : <Navigate to="/" />} />
          <Route path="/payment-reminders" element={token ? <PaymentReminders /> : <Navigate to="/" />} />
          <Route path="/categories" element={token ? <Categories /> : <Navigate to="/" />} />
          <Route path="/statistics" element={token ? <Statistics /> : <Navigate to="/" />} />
          <Route path="/ai-insights" element={token ? <AIInsightsImproved /> : <Navigate to="/" />} />
          <Route path="/goals" element={token ? <Goals /> : <Navigate to="/" />} />
          <Route path="/advanced-analytics" element={token ? <AdvancedAnalytics /> : <Navigate to="/" />} />
          <Route path="/analytics" element={<Navigate to="/advanced-analytics" replace />} />
          <Route path="/debts" element={token ? <Debts /> : <Navigate to="/" />} />
          
          {/* Ruta pública unificada de deuda y cobro compartido — sin autenticación */}
          <Route path="/public/debt/:shareToken" element={<PublicDebt />} />
          <Route path="/pago-amigo/:token" element={<PublicDebt />} />
          <Route path="/public/shared-expense/:token" element={<PublicDebt />} />
          
          <Route path="/install-pwa" element={<InstallPWA />} />
          <Route path="/feedback" element={token ? <FeedbackSupport /> : <Navigate to="/" />} />
          <Route path="/support" element={token ? <FeedbackSupport /> : <Navigate to="/" />} />
          <Route path="/profile" element={token ? <Profile /> : <Navigate to="/" />} />
          <Route path="/admin" element={token && user?.role === 'admin' ? <AdminPanel /> : <Navigate to="/" />} />
        </Routes>
        <PaymentLockOverlay />
        <PWAInstallBanner />
        <PWAUpdateNotification />
        {/* {user && !user.onboarding_completed && (
          <OnboardingTour 
            user={user} 
            onComplete={refreshUser} 
            refreshUser={refreshUser} 
          />
        )} */}
      </BrowserRouter>
      <Toaster
        position={toasterConfig.position}
        expand={toasterConfig.expand}
        richColors={toasterConfig.richColors}
        closeButton={toasterConfig.closeButton}
        duration={toasterConfig.duration}
        toastOptions={toasterConfig.toastOptions}
      />
    </AuthContext.Provider>
  );
}

export default App;